import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { useNotification } from '../../../context/NotificationContext';

// Import Icons
import { 
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const CloudflareOAuthCallback = () => {
  const notify = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [domainInfo, setDomainInfo] = useState(null);
  const [recordsCreated, setRecordsCreated] = useState([]);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        setProcessing(true);
        setError(null);

        // Parse URL parameters
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const errorParam = urlParams.get('error');

        // Check for OAuth errors
        if (errorParam) {
          throw new Error(`OAuth error: ${errorParam}`);
        }

        if (!code) {
          throw new Error('Authorization code not found');
        }

        if (!state) {
          throw new Error('State parameter not found');
        }

        // Parse state to get domain ID
        const stateData = JSON.parse(decodeURIComponent(state));
        const domainId = stateData.domainId;

        if (!domainId) {
          throw new Error('Domain ID not found in state');
        }

        // Exchange code for token and create DNS records. redirectUri MUST
        // match the one used at authorization time (this same callback route).
        const redirectUri = `${window.location.origin}/oauth/cloudflare/callback`;
        const response = await axiosInstance.post(
          `/api/business/domains/${domainId}/oauth/cloudflare/callback`,
          { code, state, redirectUri }
        );

        if (response.data.success) {
          setSuccess(true);
          setDomainInfo(response.data.domain);
          setRecordsCreated(response.data.records || []);
          
          notify('Cloudflare connected successfully! DNS records have been created.', 'success');
          
          // Redirect to domains page after a short delay
          setTimeout(() => {
            navigate('/domains', { replace: true });
          }, 3000);
        } else {
          throw new Error(response.data.message || 'Failed to process OAuth callback');
        }

      } catch (err) {
        logger.error('OAuth callback error:', err);
        setError(err.message || 'Failed to process OAuth callback');
        notify('Failed to connect Cloudflare account', 'error');
      } finally {
        setProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [location.search, navigate]);

  const handleRetry = () => {
    navigate('/domains', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#111111] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-[#1a1a1a] shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-orange-50 dark:bg-orange-900/20 p-6 text-center">
          <div className="flex justify-center mb-3">
            <CloudIcon className="h-12 w-12 text-orange-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cloudflare Integration
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Setting up DNS automation
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {processing && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Processing Authorization
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Connecting your Cloudflare account and creating DNS records...
              </p>
            </div>
          )}

          {success && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Successfully Connected!
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Your Cloudflare account has been connected and DNS records have been created.
              </p>

              {domainInfo && (
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4 mb-4 text-left">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Domain: {domainInfo.domainName}
                  </h3>
                  
                  {recordsCreated.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        DNS Records Created:
                      </h4>
                      <div className="space-y-1">
                        {recordsCreated.map((record, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-mono bg-gray-100 dark:bg-[#2a2a2a] px-1 rounded">
                              {record.type}
                            </span>
                            {' '}
                            <span className="font-mono">{record.name}</span>
                            {record.purpose && (
                              <span className="ml-2 text-gray-500">({record.purpose})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Redirecting to domains page in a few seconds...
              </p>
            </div>
          )}

          {error && (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Connection Failed
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </p>

              <div className="space-y-2">
                <button
                  onClick={handleRetry}
                  className="w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                >
                  Go to Domains
                </button>
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  You can try connecting Cloudflare again from the domain verification page.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudflareOAuthCallback;